import { Phone, WhatsappLogo, EnvelopeSimple, Clock } from "@phosphor-icons/react";

const PHONE = process.env.REACT_APP_SUPPORT_PHONE || "+919876543210";
const WA    = process.env.REACT_APP_SUPPORT_WHATSAPP || "+919876543210";
const EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "hello@talaio.com";
const HOURS = process.env.REACT_APP_SUPPORT_HOURS || "Mon–Sat, 10 AM – 7 PM IST";

const digits = (s) => (s || "").replace(/\D/g, "");
const waMessage = "Hi Talaio team, I would like to discuss the Startup Launch Kit for my business.";
const waHref = `https://wa.me/${digits(WA)}?text=${encodeURIComponent(waMessage)}`;
const telHref = `tel:${PHONE.replace(/\s/g, "")}`;
const mailHref = `mailto:${EMAIL}?subject=${encodeURIComponent("Startup Launch Kit — question")}&body=${encodeURIComponent(waMessage)}`;

export const supportLinks = { phone: PHONE, wa: WA, email: EMAIL, hours: HOURS, waHref, telHref, mailHref };

/**
 * Full contact card — sits between Pricing and Apply form.
 */
export function SupportContactBand() {
  return (
    <section className="border-b border-[var(--pf-border)] bg-[var(--pf-bg)]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="support-contact-band">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="pf-label mb-3">Not sure yet?</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-3">Talk to a human first.</h2>
            <p className="text-[var(--pf-text-2)] leading-relaxed">
              Every launch is different. Before you commit, hop on a quick WhatsApp chat or a call with our team —
              we&apos;ll walk you through what your business actually needs (and what it doesn&apos;t).
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-[var(--pf-text-3)]">
              <Clock size={14}/> {HOURS}
            </div>
          </div>
          <div className="grid gap-3">
            <a href={waHref} target="_blank" rel="noopener noreferrer"
              data-testid="support-whatsapp"
              className="group inline-flex items-center gap-4 bg-[#128C4A] hover:bg-[#0F7C41] text-white p-4 rounded-xl shadow-md hover:shadow-lg transition-all">
              <span className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <WhatsappLogo size={26} weight="fill"/>
              </span>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/75">WhatsApp — fastest</div>
                <div className="font-black text-lg tracking-tight">{WA}</div>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold opacity-80 group-hover:opacity-100">Chat →</div>
            </a>
            <a href={telHref}
              data-testid="support-phone"
              className="group inline-flex items-center gap-4 bg-white hover:bg-[var(--pf-surface-2)] border border-[var(--pf-border)] hover:border-[var(--pf-text)] p-4 rounded-xl transition-colors">
              <span className="w-12 h-12 rounded-full bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] flex items-center justify-center shrink-0">
                <Phone size={22} weight="fill"/>
              </span>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-text-3)]">Call us</div>
                <div className="font-black text-lg tracking-tight text-[var(--pf-text)]">{PHONE}</div>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--pf-text-2)] group-hover:text-[var(--pf-text)]">Tap →</div>
            </a>
            <a href={mailHref}
              data-testid="support-email"
              className="group inline-flex items-center gap-4 bg-white hover:bg-[var(--pf-surface-2)] border border-[var(--pf-border)] hover:border-[var(--pf-text)] p-4 rounded-xl transition-colors">
              <span className="w-12 h-12 rounded-full bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] flex items-center justify-center shrink-0">
                <EnvelopeSimple size={22} weight="fill"/>
              </span>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-text-3)]">Email</div>
                <div className="font-black text-base tracking-tight text-[var(--pf-text)] break-all">{EMAIL}</div>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-[var(--pf-text-2)] group-hover:text-[var(--pf-text)] shrink-0">Send →</div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Compact one-line link — sits under hero CTAs and under the apply-form submit button.
 * @param {"hero"|"form"} variant — hero uses white-on-dark, form uses default text
 */
export function SupportContactInline({ variant = "form" }) {
  const isHero = variant === "hero";
  return (
    <div className={`inline-flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${isHero ? "text-white/80" : "text-[var(--pf-text-2)]"}`}
      data-testid={`support-inline-${variant}`}>
      <span className="uppercase tracking-widest font-bold opacity-80">Have questions? Talk to us first —</span>
      <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid={`support-inline-wa-${variant}`}
        className="inline-flex items-center gap-1 font-bold text-[#25D366] hover:underline">
        <WhatsappLogo size={14} weight="fill"/> WhatsApp
      </a>
      <a href={telHref} data-testid={`support-inline-phone-${variant}`}
        className={`inline-flex items-center gap-1 font-bold hover:underline ${isHero ? "text-white" : "text-[var(--pf-text)]"}`}>
        <Phone size={13} weight="fill"/> {PHONE}
      </a>
      <a href={mailHref} data-testid={`support-inline-email-${variant}`}
        className={`inline-flex items-center gap-1 font-bold hover:underline ${isHero ? "text-white" : "text-[var(--pf-text)]"}`}>
        <EnvelopeSimple size={13} weight="fill"/> {EMAIL}
      </a>
    </div>
  );
}
